/**
 * useAssignmentEmailOptions
 * Holds the admin-editable email options shown when delegating a project or
 * work order to an external user: the message body, the Reply-To address, and
 * a "send a copy to me" toggle.
 *
 * Defaults are kept "live" rather than copied into state: the composer shows the
 * default message / Reply-To until the admin actually edits the field, at which
 * point their input sticks. This avoids races between resetting on open and
 * prefilling, and lets the default message follow the selected recipient until
 * the admin starts typing.
 *
 * The message default comes from the caller (it depends on task type + recipient
 * name); the Reply-To default is the admin's aviseringar address
 * (users/userList/{uid}/email), fetched once via getOwnEmail.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebase';

export interface AssignmentEmailOptions {
    subject: string;
    customMessage: string;
    replyTo: string;
    sendCopyToAdmin: boolean;
}

export interface AssignmentEmailDefaults {
    /** Default subject line for the current task */
    subject: string;
    /** Default message body for the current recipient */
    message: string;
}

const getOwnEmailFn = httpsCallable<Record<string, never>, { success: boolean; email?: string }>(
    functions,
    'getOwnEmail'
);

/**
 * @param open     Whether the host modal is open. Admin edits reset each time it
 *                 opens so one invite never leaks into the next.
 * @param active   Whether the external composer is engaged — gates the Reply-To
 *                 prefetch so pure internal assignments skip it.
 * @param defaults Live default subject + message for the current task/recipient.
 */
export function useAssignmentEmailOptions(
    open: boolean,
    active: boolean,
    defaults: AssignmentEmailDefaults
) {
    const [subject, setSubjectValue] = useState('');
    const [subjectTouched, setSubjectTouched] = useState(false);
    const [message, setMessage] = useState('');
    const [messageTouched, setMessageTouched] = useState(false);
    const [replyTo, setReplyTo] = useState('');
    const [replyToTouched, setReplyToTouched] = useState(false);
    // Start from the admin's login email so Reply-To is visibly prefilled
    // immediately; the aviseringar email (if set) upgrades it once fetched.
    const [defaultReplyTo, setDefaultReplyTo] = useState(() => auth.currentUser?.email || '');
    const [sendCopyToAdmin, setSendCopyToAdmin] = useState(false);
    const [loadingDefault, setLoadingDefault] = useState(false);
    const fetchedDefault = useRef(false);

    // Reset admin edits on each open (covers cancel, close, and the post-send
    // onHide, since the modal stays mounted between uses). The fetched Reply-To
    // default is intentionally kept — it's the admin's stable address.
    const wasOpen = useRef(false);
    useEffect(() => {
        if (open && !wasOpen.current) {
            setSubjectValue('');
            setSubjectTouched(false);
            setMessage('');
            setMessageTouched(false);
            setReplyTo('');
            setReplyToTouched(false);
            setSendCopyToAdmin(false);
        }
        wasOpen.current = open;
    }, [open]);

    // Upgrade the Reply-To default to the admin's aviseringar email (if set)
    // once, when the external composer is first engaged. Falls back to the login
    // email already in place.
    useEffect(() => {
        if (!active || fetchedDefault.current) return;
        fetchedDefault.current = true;
        let cancelled = false;
        setLoadingDefault(true);
        getOwnEmailFn({})
            .then((result) => {
                if (!cancelled && result.data.email) setDefaultReplyTo(result.data.email);
            })
            .catch(() => {
                // Keep the login email already prefilled.
            })
            .finally(() => {
                if (!cancelled) setLoadingDefault(false);
            });
        return () => {
            cancelled = true;
        };
    }, [active]);

    const setSubject = useCallback((value: string) => {
        setSubjectTouched(true);
        setSubjectValue(value);
    }, []);

    const setCustomMessage = useCallback((value: string) => {
        setMessageTouched(true);
        setMessage(value);
    }, []);

    const handleReplyToChange = useCallback((value: string) => {
        setReplyToTouched(true);
        setReplyTo(value);
    }, []);

    // Effective values: the admin's edit if they've touched the field, else the
    // live default.
    const options: AssignmentEmailOptions = {
        subject: subjectTouched ? subject : defaults.subject,
        customMessage: messageTouched ? message : defaults.message,
        replyTo: replyToTouched ? replyTo : defaultReplyTo,
        sendCopyToAdmin,
    };

    return {
        options,
        loadingDefault,
        setSubject,
        setCustomMessage,
        setReplyTo: handleReplyToChange,
        setSendCopyToAdmin,
    };
}
